import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useDebounce } from 'utils/useDebounce';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Breadcrumbs,
    Link,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    Toolbar,
    Tooltip,
    Button,
    Avatar,
    ListItemIcon,
    ListItemText,
    Paper,
    TablePagination,
    CircularProgress,
    FormControl,
    Select,
    InputLabel,
    Modal
} from '@mui/material';
import {
    IconFile,
    IconFileZip,
    IconPhoto,
    IconVideo,
    IconMusic,
    IconFileText,
    IconFileSpreadsheet,
    IconFileCode,
    IconSearch,
    IconUpload,
    IconDownload,
    IconTrash,
    IconEdit,
    IconCopy,
    IconCut,
    IconShare,
    IconDotsVertical,
    IconHome,
    IconChevronRight,
    IconFilter,
    IconLayoutGrid,
    IconLayoutList,
    IconRefresh,
    IconCloud,
    IconDatabase,
    IconFolder,
    IconFolderOpen,
    IconRobot
} from '@tabler/icons-react';
import {
    useGetStorageFilesQuery,
    useDeleteStorageFileMutation,
    useBulkDeleteStorageFilesMutation,
    useMoveStorageFilesMutation,
    useCopyStorageFilesMutation,
    useCreateStorageFolderMutation,
    useDeleteStorageFolderMutation,
    useGetStorageQuotaQuery,
    useGetStorageFoldersQuery,
    useLazyGetStorageFilePreviewQuery
} from 'store/slices/apiSlice';
import {
    updateFilters,
    clearFilters,
    setFiles,
    setPage,
    setPageSize,
    setLoading
} from 'store/slices/storage';
import { fetchProjects } from 'store/slices/project';
import FileSharingDialog from './FileSharingDialog';
import VirtualizedFileList from './VirtualizedFileList';
import AddToChatbotDialog from './AddToChatbotDialog';

// Map filters to API params (snake_case)
const storageQueryParams = (filters, page, pageSize) => ({
    page: page ?? 1,
    page_size: pageSize ?? 50,
    search: filters?.search || undefined,
    storage_backend: filters?.storageBackend || undefined,
    lifecycle_tier: filters?.lifecycleTier || undefined,
    migration_status: filters?.migrationStatus || undefined,
    project_id: filters?.projectId ?? undefined,
    folder_path: filters?.folderPath ?? undefined
});

const FileExplorer = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { page, pageSize, filters } = useSelector((state) => state.storage);
    const { projects } = useSelector((state) => state.project);

    const queryParams = storageQueryParams(filters, page, pageSize);
    const { data: storageData, isLoading: storageLoading, refetch: refetchFiles } = useGetStorageFilesQuery(queryParams);
    const { data: quotaData } = useGetStorageQuotaQuery(undefined, { skip: false });
    const { data: foldersData } = useGetStorageFoldersQuery({});
    const [deleteFileMutation] = useDeleteStorageFileMutation();
    const [bulkDeleteMutation] = useBulkDeleteStorageFilesMutation();
    const [moveFilesMutation] = useMoveStorageFilesMutation();
    const [copyFilesMutation] = useCopyStorageFilesMutation();
    const [createFolderMutation] = useCreateStorageFolderMutation();
    const [deleteFolderMutation] = useDeleteStorageFolderMutation();
    const [triggerPreview, { data: previewData }] = useLazyGetStorageFilePreviewQuery();

    const files = storageData?.files ?? [];
    const total = storageData?.total ?? 0;
    const totalPages = storageData?.total_pages ?? 0;
    const loading = storageLoading;
    const quota = quotaData ?? null;
    const folders = foldersData ?? [];

    useEffect(() => {
        if (storageData) dispatch(setFiles(storageData));
    }, [storageData, dispatch]);
    useEffect(() => {
        dispatch(setLoading(storageLoading));
    }, [storageLoading, dispatch]);

    const [selected, setSelected] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300); // Debounce search với 300ms
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [storageBackendFilter, setStorageBackendFilter] = useState('');
    const [lifecycleTierFilter, setLifecycleTierFilter] = useState('');
    const [migrationStatusFilter, setMigrationStatusFilter] = useState('');
    const [projectFilter, setProjectFilter] = useState('');
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [selectedFileForShare, setSelectedFileForShare] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [chatbotDialogOpen, setChatbotDialogOpen] = useState(false);

    // Đồng bộ UI filter với Redux khi điều hướng/refresh
    useEffect(() => {
        setStorageBackendFilter(filters.storageBackend || '');
        setLifecycleTierFilter(filters.lifecycleTier || '');
        setMigrationStatusFilter(filters.migrationStatus || '');
        setSearchTerm(filters.search || '');
        setProjectFilter(filters.projectId || '');
        // Sync currentFolder với folderPath filter
        if (filters.folderPath !== undefined) {
            setCurrentFolder(filters.folderPath || null);
        }
    }, [filters]);

    useEffect(() => {
        dispatch(fetchProjects());
    }, [dispatch]);

    const handleShareFile = (file) => {
        setSelectedFileForShare(file);
        setShareDialogOpen(true);
    };

    useEffect(() => {
        if (debouncedSearchTerm !== searchTerm) {
            dispatch(updateFilters({ search: debouncedSearchTerm }));
            dispatch(setPage(1));
        }
    }, [debouncedSearchTerm, searchTerm, pageSize, dispatch]);

    // Extract folder from storage_key if folder_path is not set
    const getFileFolder = (file) => {
        if (file.folder_path) {
            return file.folder_path;
        }
        // Parse from storage_key: /path/to/datahub/{user_token}/{project_id}/{folder}/.../filename
        if (file.storage_key) {
            const parts = file.storage_key.split('/');
            // Find project name in path
            const projectName = projectFilter || filters.projectId;
            if (projectName) {
                const projectIdx = parts.findIndex(p => p === projectName);
                if (projectIdx >= 0 && projectIdx + 1 < parts.length - 1) {
                    // Extract full folder path relative to project
                    const folderParts = parts.slice(projectIdx + 1, -1);
                    if (folderParts.length > 0) {
                        const folderPath = folderParts.join('/');
                        // Exclude known system folders
                        if (!folderPath.startsWith('.') && folderPath !== 'datahub' && folderPath !== 'uploads') {
                            return folderPath;
                        }
                    }
                }
            }
        }
        return null; // Root level
    };
    
    // Get immediate subfolder from folder_path (for grouping)
    // e.g., "jupyterhub/white-blood-cell-1/test" -> "white-blood-cell-1" when currentFolder="jupyterhub"
    const getImmediateSubfolder = (fileFolder, currentFolderPath) => {
        if (!fileFolder || !currentFolderPath) return null;
        if (fileFolder.startsWith(currentFolderPath + '/')) {
            const remaining = fileFolder.substring(currentFolderPath.length + 1);
            const parts = remaining.split('/');
            return parts[0]; // First subfolder
        }
        return null;
    };

    // Group files by folder (supports nested folders)
    const groupFilesByFolder = (files, currentFolderPath = null) => {
        const folders = new Map(); // Map of immediate subfolders
        const rootFiles = [];
        
        files.forEach(file => {
            const fileFolder = getFileFolder(file);
            
            if (currentFolderPath) {
                // We're inside a folder - group by immediate subfolders
                const subfolder = getImmediateSubfolder(fileFolder, currentFolderPath);
                if (subfolder) {
                    // File is in a subfolder
                    if (!folders.has(subfolder)) {
                        folders.set(subfolder, []);
                    }
                    folders.get(subfolder).push(file);
                } else if (fileFolder === currentFolderPath) {
                    // File is directly in current folder (not in subfolder)
                    rootFiles.push(file);
                }
            } else {
                // We're at root - group by top-level folders
                if (fileFolder) {
                    // Extract top-level folder (first part before /)
                    const topLevelFolder = fileFolder.split('/')[0];
                    if (!folders.has(topLevelFolder)) {
                        folders.set(topLevelFolder, []);
                    }
                    folders.get(topLevelFolder).push(file);
                } else {
                    rootFiles.push(file);
                }
            }
        });
        
        return { folders, rootFiles };
    };

    const handleFolderClick = (folderPath) => {
        setCurrentFolder(folderPath);
        dispatch(updateFilters({ folderPath: folderPath || null }));
        dispatch(setPage(1));
    };

    const handlePageChange = (event, newPage) => {
        dispatch(setPage(newPage + 1));
    };

    const handlePageSizeChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        dispatch(setPageSize(newSize));
        dispatch(setPage(1));
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        // Không dispatch ngay, đợi debounce
    };

    const handleFilterChange = () => {
        dispatch(
            updateFilters({
                storageBackend: storageBackendFilter || null,
                lifecycleTier: lifecycleTierFilter || null,
                migrationStatus: migrationStatusFilter || null,
                projectId: projectFilter || null
            })
        );
        dispatch(setPage(1));
    };

    const handleClearFilters = () => {
        setStorageBackendFilter('');
        setLifecycleTierFilter('');
        setMigrationStatusFilter('');
        setProjectFilter('');
        setSearchTerm('');
        dispatch(clearFilters());
        dispatch(setPage(1));
    };

    const getFileIcon = (contentType) => {
        if (!contentType) return <IconFile size={20} />;

        if (contentType.startsWith('image/')) return <IconPhoto size={20} />;
        if (contentType.startsWith('video/')) return <IconVideo size={20} />;
        if (contentType.startsWith('audio/')) return <IconMusic size={20} />;

        switch (contentType) {
            case 'application/pdf':
            case 'application/msword':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return <IconFileText size={20} />;
            case 'application/zip':
            case 'application/x-zip-compressed':
            case 'application/x-rar-compressed':
                return <IconFileZip size={20} />;
            case 'text/csv':
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return <IconFileSpreadsheet size={20} />;
            case 'text/javascript':
            case 'application/javascript':
            case 'text/x-python':
            case 'text/html':
            case 'text/css':
                return <IconFileCode size={20} />;
            default:
                return <IconFile size={20} />;
        }
    };

    const getFileColor = (contentType) => {
        if (!contentType) return theme.palette.grey[500];

        if (contentType.startsWith('image/')) return theme.palette.success.main;
        if (contentType.startsWith('video/')) return theme.palette.secondary.main;
        if (contentType.startsWith('audio/')) return theme.palette.warning.main;

        if (contentType.includes('pdf') || contentType.includes('word')) return theme.palette.info.main;
        if (contentType.includes('zip') || contentType.includes('compressed')) return theme.palette.error.main;
        if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv'))
            return theme.palette.success.dark;
        if (contentType.includes('javascript') || contentType.includes('python') || contentType.includes('html'))
            return theme.palette.primary.main;

        return theme.palette.grey[500];
    };

    const getMigrationStatusChip = (status) => {
        const statusConfig = {
            completed: { label: 'Migrated', color: 'success' },
            in_progress: { label: 'Migrating', color: 'warning' },
            pending: { label: 'Pending', color: 'default' },
            failed: { label: 'Failed', color: 'error' }
        };

        const config = statusConfig[status] || { label: status || 'N/A', color: 'default' };
        return <Chip label={config.label} size="small" color={config.color} sx={{ height: 20, fontSize: '0.7rem' }} />;
    };

    const getLifecycleTierChip = (tier) => {
        const tierConfig = {
            hot: { label: 'Hot', color: theme.palette.error.main },
            warm: { label: 'Warm', color: theme.palette.warning.main },
            cold: { label: 'Cold', color: theme.palette.info.main },
            archive: { label: 'Archive', color: theme.palette.grey[500] }
        };

        const config = tierConfig[tier] || { label: tier || 'N/A', color: theme.palette.grey[500] };
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: `${config.color}20`,
                    color: config.color,
                    fontWeight: 600
                }}
            />
        );
    };

    const getStorageBackendChip = (backend, lakehouseBucket) => {
        if (backend === 'minio-lakehouse' || lakehouseBucket) {
            return (
                <Chip
                    icon={<IconCloud size={14} />}
                    label="Lakehouse"
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                />
            );
        }
        return <Chip icon={<IconDatabase size={14} />} label={backend || 'Legacy'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />;
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelected(files && files.length > 0 ? files.map((file) => file.id) : []);
        } else {
            setSelected([]);
        }
    };

    const handleSelect = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleContextMenu = (event, file) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        setSelectedFile(file);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedFile(null);
    };

    const handleToggleStar = (fileId) => {
        // Toggle star logic
        console.log('Toggle star for file:', fileId);
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        try {
            await deleteFileMutation(fileId).unwrap();
            setSelected((s) => s.filter((id) => id !== fileId));
        } catch (error) {
            console.error('Error deleting file:', error);
            alert(error?.data?.detail || 'Delete failed');
        }
    };

    const handleBulkDelete = async () => {
        if (selected.length === 0) return;
        const confirmMessage = selected.length === 1
            ? 'Are you sure you want to delete this file?'
            : `Are you sure you want to delete ${selected.length} files?`;
        if (!window.confirm(confirmMessage)) return;
        try {
            const result = await bulkDeleteMutation({ file_ids: selected }).unwrap();
            if (result?.successful > 0) setSelected([]);
            if (result?.failed > 0) alert(`${result.failed} file(s) failed to delete.`);
        } catch (error) {
            console.error('Error bulk deleting files:', error);
            alert(error?.data?.detail || 'Failed to delete files.');
        }
    };

    const handleFilePreview = async (file) => {
        try {
            const result = await triggerPreview(file.id);
            if (result?.data) {
                setPreviewUrl(result.data.preview_url ?? null);
                setPreviewFile(file);
            }
        } catch (error) {
            console.error('Error getting preview:', error);
            alert('Failed to load file preview');
        }
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
        setPreviewUrl(null);
    };

    return (
        <Card>
            <CardContent>
                {/* Compact Quota Display */}
                {quota && (
                    <Box sx={{ 
                        px: 2, 
                        py: 0.5, 
                        bgcolor: 'background.default', 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        justifyContent: 'flex-end'
                    }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                            Storage:
                        </Typography>
                        <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                            {quota.used_formatted} / {quota.quota_formatted}
                        </Typography>
                        <Box sx={{ width: 100, height: 4, bgcolor: 'grey.200', borderRadius: 0.5, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    width: `${Math.min(quota.usage_percentage, 100)}%`,
                                    height: '100%',
                                    bgcolor: quota.usage_percentage > 90 ? 'error.main' : quota.usage_percentage > 75 ? 'warning.main' : 'primary.main',
                                    transition: 'width 0.3s'
                                }}
                            />
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', minWidth: '35px' }}>
                            {quota.usage_percentage.toFixed(0)}%
                        </Typography>
                    </Box>
                )}

                {/* Toolbar */}
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                    <Box sx={{ flex: '1 1 100%' }}>
                        {selected.length > 0 ? (
                            <Typography color="inherit" variant="subtitle1" component="div">
                                {selected.length} selected
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Breadcrumbs separator={<IconChevronRight size={16} />} aria-label="breadcrumb">
                                    <Link 
                                        underline="hover" 
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} 
                                        color="inherit"
                                        onClick={() => {
                                            setCurrentFolder(null);
                                            dispatch(updateFilters({ folderPath: null }));
                                            dispatch(setPage(1));
                                        }}
                                    >
                                        <IconHome size={16} style={{ marginRight: 4 }} />
                                        {projectFilter ? `Project: ${projects?.find(p => (p.id || p.project_name) === projectFilter)?.project_name || projectFilter}` : 'Home'}
                                    </Link>
                                    {currentFolder && (
                                        <>
                                            {currentFolder.split('/').map((folder, index, arr) => {
                                                const path = arr.slice(0, index + 1).join('/');
                                                return (
                                                    <Link
                                                        key={path}
                                                        underline="hover"
                                                        sx={{ cursor: 'pointer' }}
                                                        color={index === arr.length - 1 ? 'text.primary' : 'inherit'}
                                                        onClick={() => handleFolderClick(path)}
                                                    >
                                                        {folder}
                                                    </Link>
                                                );
                                            })}
                                        </>
                                    )}
                                </Breadcrumbs>
                                {projectFilter && (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>•</Typography>
                                        <Chip 
                                            label={`Project: ${projects?.find(p => (p.id || p.project_name) === projectFilter)?.project_name || projectFilter}`}
                                            size="small"
                                            onDelete={() => {
                                                setProjectFilter('');
                                                dispatch(updateFilters({ projectId: null }));
                                                dispatch(setPage(1));
                                            }}
                                        />
                                    </>
                                )}
                                {!projectFilter && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        (Showing all files)
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>

                    {selected.length > 0 ? (
                        <>
                            <Tooltip title="Download">
                                <IconButton>
                                    <IconDownload size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Add to Chatbot (RAG)">
                                <IconButton 
                                    onClick={() => {
                                        const selectedFiles = files.filter(f => selected.includes(f.id));
                                        if (selectedFiles.length > 0) {
                                            setChatbotDialogOpen(true);
                                        }
                                    }}
                                    color="primary"
                                >
                                    <IconRobot size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Share Selected">
                                <IconButton onClick={() => {
                                    if (selected.length === 1) {
                                        const file = files.find(f => f.id === selected[0]);
                                        if (file) handleShareFile(file);
                                    }
                                }}>
                                    <IconShare size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Move to Folder">
                                <IconButton onClick={async () => {
                                    const folder = prompt('Enter destination folder path:');
                                    if (folder) {
                                        try {
                                            await moveFilesMutation({ file_ids: selected, destination_path: folder }).unwrap();
                                            setSelected([]);
                                        } catch (error) {
                                            alert(error?.data?.detail || 'Failed to move files');
                                        }
                                    }
                                }}>
                                    <IconCut size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy to Folder">
                                <IconButton onClick={async () => {
                                    const folder = prompt('Enter destination folder path:');
                                    if (folder) {
                                        try {
                                            await copyFilesMutation({ file_ids: selected, destination_path: folder }).unwrap();
                                            setSelected([]);
                                        } catch (error) {
                                            alert('Failed to copy files');
                                        }
                                    }
                                }}>
                                    <IconCopy size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton color="error" onClick={handleBulkDelete}>
                                    <IconTrash size={20} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <TextField
                                placeholder="Search files..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                sx={{ mr: 2, width: 300 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconSearch size={18} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Tooltip title="Upload files">
                                <IconButton color="primary">
                                    <IconUpload size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Refresh">
                                <IconButton onClick={() => refetchFiles()} disabled={loading}>
                                    <IconRefresh size={20} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={viewMode === 'list' ? 'Grid view' : 'List view'}>
                                <IconButton onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}>
                                    {viewMode === 'list' ? <IconLayoutGrid size={20} /> : <IconLayoutList size={20} />}
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Toolbar>

                {/* Filters */}
                <Box
                    sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}
                >
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Project</InputLabel>
                        <Select
                            value={projectFilter}
                            label="Project"
                            onChange={(e) => {
                                const value = e.target.value;
                                setProjectFilter(value);
                                // Auto-apply filter when project is selected
                                if (value) {
                                    dispatch(updateFilters({ projectId: value }));
                                    dispatch(setPage(1));
                                } else {
                                    dispatch(updateFilters({ projectId: null }));
                                    dispatch(setPage(1));
                                }
                            }}
                        >
                            <MenuItem value="">All Projects</MenuItem>
                            {projects && Array.isArray(projects) && projects.map((project) => {
                                // Use project_name as identifier (matches database storage)
                                const projectIdentifier = project.project_name || project.id;
                                return (
                                    <MenuItem key={projectIdentifier} value={projectIdentifier}>
                                        {project.project_name || project.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Storage Backend</InputLabel>
                        <Select
                            value={storageBackendFilter}
                            label="Storage Backend"
                            onChange={(e) => setStorageBackendFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="minio-lakehouse">Lakehouse</MenuItem>
                            <MenuItem value="gcs">GCS</MenuItem>
                            <MenuItem value="s3">S3</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Lifecycle Tier</InputLabel>
                        <Select value={lifecycleTierFilter} label="Lifecycle Tier" onChange={(e) => setLifecycleTierFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="hot">Hot</MenuItem>
                            <MenuItem value="warm">Warm</MenuItem>
                            <MenuItem value="cold">Cold</MenuItem>
                            <MenuItem value="archive">Archive</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Migration Status</InputLabel>
                        <Select
                            value={migrationStatusFilter}
                            label="Migration Status"
                            onChange={(e) => setMigrationStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="completed">Migrated</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" size="small" onClick={handleFilterChange} startIcon={<IconFilter size={16} />}>
                        Apply
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleClearFilters}>
                        Clear
                    </Button>
                    {loading && <CircularProgress size={24} />}
                </Box>

                {/* File List/Grid */}
                {viewMode === 'list' ? (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selected.length > 0 && files && selected.length < files.length}
                                            checked={files && files.length > 0 && selected.length === files.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'name'}
                                            direction={sortBy === 'name' ? sortOrder : 'asc'}
                                            onClick={() => {
                                                setSortBy('name');
                                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                            }}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Owner</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'modified'}
                                            direction={sortBy === 'modified' ? sortOrder : 'asc'}
                                            onClick={() => {
                                                setSortBy('modified');
                                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                            }}
                                        >
                                            Modified
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'size'}
                                            direction={sortBy === 'size' ? sortOrder : 'asc'}
                                            onClick={() => {
                                                setSortBy('size');
                                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                                            }}
                                        >
                                            Size
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Storage</TableCell>
                                    <TableCell>Bucket</TableCell>
                                    <TableCell>Migration</TableCell>
                                    <TableCell>Tier</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    if (!files || files.length === 0) {
                                        return (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        No files found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    
                                    // Group files by folder if not in a specific folder
                                    let filesToShow = files;
                                    let folders = new Map();
                                    let rootFiles = [];
                                    
                                    if (!currentFolder) {
                                        // Not in a folder - show folders and root files
                                        const grouped = groupFilesByFolder(files, null);
                                        folders = grouped.folders;
                                        rootFiles = grouped.rootFiles;
                                        filesToShow = rootFiles;
                                    } else {
                                        // In a folder - group by immediate subfolders
                                        const grouped = groupFilesByFolder(files, currentFolder);
                                        folders = grouped.folders;
                                        rootFiles = grouped.rootFiles;
                                        filesToShow = rootFiles;
                                    }
                                    
                                    const result = [];
                                    
                                    // Show folders first (both at root and inside folders)
                                    if (folders.size > 0) {
                                        Array.from(folders.entries()).sort().forEach(([folderName, folderFiles]) => {
                                            // Build full folder path for navigation
                                            const fullFolderPath = currentFolder 
                                                ? `${currentFolder}/${folderName}` 
                                                : folderName;
                                            
                                            result.push(
                                                <TableRow
                                                    key={`folder-${fullFolderPath}`}
                                                    hover
                                                    sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
                                                    onClick={() => handleFolderClick(fullFolderPath)}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox disabled />
                                                    </TableCell>
                                                    <TableCell colSpan={9}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                                                <IconFolder size={18} style={{ color: 'white' }} />
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" fontWeight="bold">
                                                                    {folderName}/
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {folderFiles.length} file{folderFiles.length !== 1 ? 's' : ''}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    }
                                    filesToShow.forEach((file) => {
                                        const isSelected = selected.indexOf(file.id) !== -1;
                                        result.push(
                                            <TableRow
                                                key={file.id}
                                                hover
                                                selected={isSelected}
                                                onContextMenu={(e) => handleContextMenu(e, file)}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox checked={isSelected} onChange={() => handleSelect(file.id)} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: `${getFileColor(file.content_type)}20`,
                                                                width: 32,
                                                                height: 32
                                                            }}
                                                        >
                                                            <Box sx={{ color: getFileColor(file.content_type) }}>
                                                                {getFileIcon(file.content_type)}
                                                            </Box>
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2">{file.filename}</Typography>
                                                            {file.file_version > 1 && (
                                                                <Typography variant="caption" color="textSecondary">
                                                                    v{file.file_version}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{file.user_id || 'System'}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{formatDate(file.updated_at || file.created_at)}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{formatFileSize(file.size)}</Typography>
                                                </TableCell>
                                                <TableCell>{getStorageBackendChip(file.storage_backend, file.lakehouse_bucket)}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {file.lakehouse_bucket || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{getMigrationStatusChip(file.migration_status)}</TableCell>
                                                <TableCell>{getLifecycleTierChip(file.lifecycle_tier)}</TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Add to Chatbot (RAG)">
                                                            <IconButton 
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => {
                                                                    setSelected([file.id]);
                                                                    setChatbotDialogOpen(true);
                                                                }}
                                                            >
                                                                <IconRobot size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Preview">
                                                            <IconButton 
                                                                size="small" 
                                                                color="primary"
                                                                onClick={() => handleFilePreview(file)}
                                                            >
                                                                <IconFile size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Share">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => handleShareFile(file)}
                                                            >
                                                                <IconShare size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Download">
                                                            <IconButton size="small">
                                                                <IconDownload size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteFile(file.id)}>
                                                                <IconTrash size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="More">
                                                            <IconButton size="small" onClick={(e) => handleContextMenu(e, file)}>
                                                                <IconDotsVertical size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    });
                                    
                                    return result;
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : null}

                {/* Pagination */}
                {viewMode === 'list' && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page - 1}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handlePageSizeChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                )}

                {/* Grid View - Simplified */}
                {viewMode === 'grid' && (
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        {files && files.length > 0 ? files.map((file) => {
                            const isSelected = selected.indexOf(file.id) !== -1;
                            return (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                            '&:hover': {
                                                bgcolor: 'action.hover'
                                            }
                                        }}
                                        onContextMenu={(e) => handleContextMenu(e, file)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleSelect(file.id)}
                                            sx={{ position: 'absolute', top: 4, left: 4 }}
                                            size="small"
                                        />
                                        {file.lakehouse_bucket && (
                                            <Chip
                                                icon={<IconCloud size={12} />}
                                                label="Lakehouse"
                                                size="small"
                                                color="primary"
                                                sx={{ position: 'absolute', top: 8, right: 8, height: 16, fontSize: '0.6rem' }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mb: 1,
                                                mt: 2
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor: `${getFileColor(file.content_type)}20`,
                                                    width: 48,
                                                    height: 48
                                                }}
                                            >
                                                <Box sx={{ color: getFileColor(file.content_type) }}>
                                                    {React.cloneElement(getFileIcon(file.content_type), { size: 24 })}
                                                </Box>
                                            </Avatar>
                                        </Box>
                                        <Typography variant="caption" noWrap>
                                            {file.filename}
                                        </Typography>
                                        <Typography variant="caption" display="block" color="textSecondary">
                                            {formatFileSize(file.size)}
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>{getLifecycleTierChip(file.lifecycle_tier)}</Box>
                                    </Paper>
                                </Grid>
                            );
                        }) : (
                            <Grid item xs={12}>
                                <Box textAlign="center" py={4}>
                                    <Typography variant="body2" color="textSecondary">
                                        No files found
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* Context Menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                    <MenuItem onClick={() => {
                        if (selectedFile) {
                            setSelected([selectedFile.id]);
                            setChatbotDialogOpen(true);
                        }
                        handleCloseMenu();
                    }}>
                        <ListItemIcon>
                            <IconRobot size={18} />
                        </ListItemIcon>
                        <ListItemText>Add to Chatbot (RAG)</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu}>
                        <ListItemIcon>
                            <IconDownload size={18} />
                        </ListItemIcon>
                        <ListItemText>Download</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleShareFile(selectedFile);
                        handleCloseMenu();
                    }}>
                        <ListItemIcon>
                            <IconShare size={18} />
                        </ListItemIcon>
                        <ListItemText>Share</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu}>
                        <ListItemIcon>
                            <IconEdit size={18} />
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu}>
                        <ListItemIcon>
                            <IconCopy size={18} />
                        </ListItemIcon>
                        <ListItemText>Copy</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu}>
                        <ListItemIcon>
                            <IconCut size={18} />
                        </ListItemIcon>
                        <ListItemText>Cut</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCloseMenu} sx={{ color: 'error.main' }}>
                        <ListItemIcon>
                            <IconTrash size={18} color={theme.palette.error.main} />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>

                {/* File Sharing Dialog */}
                <FileSharingDialog
                    open={shareDialogOpen}
                    onClose={() => {
                        setShareDialogOpen(false);
                        setSelectedFileForShare(null);
                    }}
                    fileId={selectedFileForShare?.id}
                    fileName={selectedFileForShare?.filename}
                />

                {/* Add to Chatbot Dialog */}
                <AddToChatbotDialog
                    open={chatbotDialogOpen}
                    onClose={() => {
                        setChatbotDialogOpen(false);
                        setSelected([]);
                    }}
                    files={files.filter(f => selected.includes(f.id))}
                    onAdd={async (data) => {
                        // Success callback - show notification
                        console.log('Successfully added documents to chatbot:', data);
                        // Could add toast notification here
                    }}
                />

                {/* File Preview Modal */}
                {previewFile && (
                    <Modal
                        open={!!previewFile}
                        onClose={handleClosePreview}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '90%',
                                maxWidth: '1200px',
                                maxHeight: '90vh',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 3,
                                borderRadius: 2,
                                overflow: 'auto',
                                outline: 'none'
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">{previewFile.filename}</Typography>
                                <IconButton onClick={handleClosePreview}>
                                    <IconFile size={20} />
                                </IconButton>
                            </Box>
                            
                            {/* Preview Content */}
                            {!previewUrl ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {previewFile.content_type?.startsWith('image/') && (
                                        <Box display="flex" justifyContent="center">
                                            <img 
                                                src={previewUrl} 
                                                alt={previewFile.filename}
                                                style={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: '70vh', 
                                                    objectFit: 'contain' 
                                                }} 
                                            />
                                        </Box>
                                    )}
                                    
                                    {previewFile.content_type === 'application/pdf' && (
                                        <Box sx={{ height: '70vh' }}>
                                            <iframe 
                                                src={previewUrl} 
                                                width="100%" 
                                                height="100%"
                                                style={{ border: 'none' }}
                                                title={previewFile.filename}
                                            />
                                        </Box>
                                    )}
                                    
                                    {(previewFile.content_type?.startsWith('text/') || 
                                      previewFile.content_type === 'application/json' ||
                                      previewFile.content_type === 'application/xml') && (
                                        <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                                            <Paper sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                <Typography variant="body2" component="pre" sx={{ 
                                                    whiteSpace: 'pre-wrap', 
                                                    wordBreak: 'break-word',
                                                    fontFamily: 'monospace',
                                                    m: 0
                                                }}>
                                                    Loading text content...
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    )}
                                    
                                    {!previewFile.content_type?.startsWith('image/') && 
                                     previewFile.content_type !== 'application/pdf' &&
                                     !previewFile.content_type?.startsWith('text/') &&
                                     previewFile.content_type !== 'application/json' &&
                                     previewFile.content_type !== 'application/xml' && (
                                        <Box textAlign="center" py={4}>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                Preview not available for this file type.
                                            </Typography>
                                            <Button 
                                                variant="contained" 
                                                startIcon={<IconDownload size={18} />}
                                                sx={{ mt: 2 }}
                                                onClick={() => window.open(previewUrl, '_blank')}
                                            >
                                                Download File
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Modal>
                )}
            </CardContent>
        </Card>
    );
};

export default FileExplorer;
